import { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useSession } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import {
  RiDatabase2Line,
  RiLoader2Line,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiTableLine,
  RiArrowUpLine,
  RiArrowDownLine,
} from '@remixicon/react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

export function DataBrowserPage() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const databaseId = params.id as string;

  const [databases, setDatabases] = useState<any[]>([]);
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string>(databaseId || '');
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [tableData, setTableData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(!databaseId);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  useEffect(() => {
    if (!databaseId && session?.user?.id) {
      loadDatabases();
    }
  }, [databaseId, session?.user?.id]);

  useEffect(() => {
    if (selectedDatabaseId) {
      loadTables();
    }
  }, [selectedDatabaseId]);

  useEffect(() => {
    if (selectedTable && selectedDatabaseId) {
      loadTableData();
    }
  }, [selectedTable, page, limit, sortBy, sortOrder]);

  const loadDatabases = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoadingDatabases(true);
      const result = await api.listDatabases(session.user.id);
      setDatabases(result.databases);
      if (result.databases.length > 0 && !databaseId) {
        setSelectedDatabaseId(result.databases[0].id);
      }
    } catch (error: any) {
      toast.error('Failed to load databases');
    } finally {
      setIsLoadingDatabases(false);
    }
  };

  const loadTables = async () => {
    if (!selectedDatabaseId) return;

    try {
      setIsLoadingTables(true);
      const result = await api.getTables(selectedDatabaseId);
      setTables(result.tables);
      if (result.tables.length > 0) {
        setSelectedTable(result.tables[0]);
      }
    } catch (error: any) {
      toast.error('Failed to load tables');
    } finally {
      setIsLoadingTables(false);
    }
  };

  const loadTableData = async () => {
    if (!selectedTable || !selectedDatabaseId) return;

    try {
      setIsLoadingData(true);
      const result = await api.getTableData(selectedDatabaseId, selectedTable, {
        page,
        limit,
        sortBy,
        sortOrder,
      });

      setTableData(result.rows);
      setTotal(result.total);

      // Extract columns from first row
      if (result.rows.length > 0) {
        setColumns(Object.keys(result.rows[0]));
      } else {
        setColumns([]);
      }
    } catch (error: any) {
      toast.error('Failed to load table data');
      setTableData([]);
      setColumns([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'NULL';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    return String(value);
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 z-10">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-4 mr-2" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => navigate({ to: '/databases' })}>
                  Databases
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Data Browser</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto">
            <ModeToggle />
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto md:p-8 lg:p-10">
          <div className="max-w-[1600px] mx-auto space-y-6">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center size-12 rounded-xl bg-primary/10">
                  <RiTableLine className="size-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Data Browser</h1>
                  <p className="text-muted-foreground">Browse and explore your table data</p>
                </div>
              </div>
            </motion.div>

            {/* Database and Table Selector */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {!databaseId && (
                      <Select
                        value={selectedDatabaseId}
                        onValueChange={(value) => {
                          setSelectedDatabaseId(value);
                          setSelectedTable('');
                          setTableData([]);
                        }}
                        disabled={isLoadingDatabases}
                      >
                        <SelectTrigger className="w-[280px]">
                          <SelectValue placeholder="Select a database" />
                        </SelectTrigger>
                        <SelectContent>
                          {databases.map((db) => (
                            <SelectItem key={db.id} value={db.id}>
                              <div className="flex items-center gap-2">
                                <RiDatabase2Line className="size-4" />
                                {db.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    <Select
                      value={selectedTable}
                      onValueChange={setSelectedTable}
                      disabled={isLoadingTables || !selectedDatabaseId}
                    >
                      <SelectTrigger className="w-[280px]">
                        <SelectValue placeholder="Select a table" />
                      </SelectTrigger>
                      <SelectContent>
                        {tables.map((table) => (
                          <SelectItem key={table} value={table}>
                            <div className="flex items-center gap-2">
                              <RiTableLine className="size-4" />
                              {table}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {!isLoadingData && tableData.length > 0 && (
                      <Badge variant="secondary" className="text-sm">
                        {total.toLocaleString()} rows total
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={limit.toString()}
                      onValueChange={(value) => {
                        setLimit(parseInt(value));
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Data Table */}
            {isLoadingData ? (
              <div className="flex items-center justify-center py-12">
                <RiLoader2Line className="size-8 animate-spin text-primary" />
              </div>
            ) : tableData.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <RiDatabase2Line className="size-16 text-muted-foreground/20 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No data found</h3>
                  <p className="text-muted-foreground">This table is empty or doesn't exist</p>
                </CardContent>
              </Card>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Card>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[calc(100vh-28rem)] w-full">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead className="w-12 text-center bg-muted/50">#</TableHead>
                            {columns.map((column) => (
                              <TableHead
                                key={column}
                                className="cursor-pointer hover:bg-muted/50 transition-colors bg-muted/50"
                                onClick={() => handleSort(column)}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold">{column}</span>
                                  {sortBy === column && (
                                    <span>
                                      {sortOrder === 'ASC' ? (
                                        <RiArrowUpLine className="size-4" />
                                      ) : (
                                        <RiArrowDownLine className="size-4" />
                                      )}
                                    </span>
                                  )}
                                </div>
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tableData.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              <TableCell className="text-center text-muted-foreground font-mono text-xs">
                                {(page - 1) * limit + rowIndex + 1}
                              </TableCell>
                              {columns.map((column) => (
                                <TableCell
                                  key={column}
                                  className={`font-mono text-sm ${
                                    row[column] === null || row[column] === undefined
                                      ? 'text-muted-foreground italic'
                                      : ''
                                  }`}
                                >
                                  {formatCellValue(row[column])}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Pagination */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
                    {total.toLocaleString()} rows
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <RiArrowLeftLine className="size-4 mr-1" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {page} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                    >
                      Next
                      <RiArrowRightLine className="size-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
